import React from 'react'
import { ControlledModal, ModalTitle } from '../../layouts/components/modal'
import { Logo } from '../../components/simple/logo'
import { pluralize } from '../../utils'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { Button } from '../../components/simple/button'
import { useShowWithPriority } from '../../providers/modalPriorityProvider'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { useAppContext } from '../../providers/context'

export const NewYearModal: React.FC<{
  classes: {
    name: string
    studentsCount: number
    otherTeachersCount: number
    id: number
  }[]
  deleteGroup: (gID: number) => Promise<boolean>
}> = ({ classes: originalClasses, deleteGroup }) => {
  const [classes, setClasses] = React.useState(originalClasses)
  const [loading, setLoading] = React.useState(false)
  const { active, onComplete } = useShowWithPriority(700)

  const { setState } = useAppContext()

  const onClose = () => {
    onComplete()
    makeRequest('/frontend_api/markOnboardingComplete', { onboarding: 'newYear' }).catch(showAxiosErrorAsToast)
    setState((s) => s && { ...s, user: s.user && { ...s.user, newYear: undefined } })
  }

  return (
    <ControlledModal
      size="md"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={active}
      disabled={loading}
    >
      <div className="flex items-center gap-2">
        <Logo />
        <ModalTitle className="mr-4 text-xl font-bold">
          Welcome back! 🎉🎉🎉
          <br />
          Thank you for using ReachMyTeach!
        </ModalTitle>
      </div>

      {classes.length > 0 ? (
        <>
          <div className="flex flex-col gap-2">
            <div className="text-lg font-bold">To get ready for the new year, delete any groups you don't want:</div>
            <div>
              If you want to delete them later, you can always edit and delete groups from the list on the left of your
              screen
            </div>
          </div>
          {classes.map((c) => (
            <div
              className="mt-4 flex items-center justify-between rounded-md p-1 shadow-[0_0_0_1px_#e1e6ef]"
              key={c.id}
            >
              <div className="ml-4 flex items-center gap-3">
                {c.name}
                <Badge badgeStyle={BadgeStyle.warning}>{c.studentsCount} students</Badge>
                {c.otherTeachersCount > 0 && (
                  <Badge badgeStyle={BadgeStyle.mint}>
                    {c.otherTeachersCount} other {pluralize(c.otherTeachersCount, 'teacher')}
                  </Badge>
                )}
              </div>
              <Button
                logo
                onClick={async () => {
                  setLoading(true)
                  if (await deleteGroup(c.id)) setClasses((ac) => ac.filter((o) => c !== o))
                  setLoading(false)
                }}
              >
                Delete
              </Button>
            </div>
          ))}
          <Button logo className="mt-3 self-center" onClick={() => setClasses([])}>
            Skip
          </Button>
        </>
      ) : (
        <>
          <div>
            If you ever get stuck, need a step-by-step guide, or need personalized support, <br />
            please click the lifesaver in the top right to access our helpdesk!
          </div>
          <Button className="self-center" logo onClick={onClose}>
            Done
          </Button>
        </>
      )}
    </ControlledModal>
  )
}
